import type {
  ISecapProductDataSource,
  ISecapRuntimeCatalog,
  ISecapRuntimeIndicator,
  SecapSourceStrategy,
} from '../../interfaces/secap/ISecapApi';
import type {
  ISecapInternalSourceRowViewModel,
  ISecapSourceRowViewModel,
  ISecapSourcesViewModel,
  SecapSourceDisplayStatus,
} from '../../interfaces/secap/ISecapSources';

const SOURCE_TYPE_LABELS = {
  api: 'API',
  ogc: 'Servizio OGC',
  open_data: 'Open data',
  periodic_import: 'Import periodico',
  user_upload: 'Caricamento ente',
} as const;

const ROLE_LABELS = {
  plan_source: 'Fonte del piano',
  comparison: 'Confronto',
} as const;

const REFRESH_LABELS = {
  manual: 'Manuale',
  daily: 'Giornaliero',
  weekly: 'Settimanale',
  monthly: 'Mensile',
  quarterly: 'Trimestrale',
  yearly: 'Annuale',
} as const;

const INGESTION_STATUS_LABELS = {
  queued: 'In coda',
  running: 'In corso',
  completed: 'Completato',
  failed: 'Fallito',
} as const;

const STATUS_LABELS: Readonly<Record<SecapSourceDisplayStatus, string>> = {
  active: 'Attiva',
  updated: 'Aggiornata',
  queued: 'In coda',
  running: 'In aggiornamento',
  error: 'Errore',
  inactive: 'Disattivata',
};

const getSourceStatus = (source: ISecapProductDataSource): SecapSourceDisplayStatus => {
  if (!source.is_active) {
    return 'inactive';
  }
  if (source.last_attempt_status === 'failed' || source.last_error) {
    return 'error';
  }
  if (source.last_attempt_status === 'running') {
    return 'running';
  }
  if (source.last_attempt_status === 'queued') {
    return 'queued';
  }
  if (source.last_attempt_status === 'completed' || source.last_success_at) {
    return 'updated';
  }
  return 'active';
};

const toSourceRow = (source: ISecapProductDataSource): ISecapSourceRowViewModel => {
  const status = getSourceStatus(source);
  return {
    uuid: source.uuid,
    code: source.code,
    name: source.name,
    sourceType: source.source_type,
    sourceTypeLabel: SOURCE_TYPE_LABELS[source.source_type],
    role: source.current_config.role,
    roleLabel: ROLE_LABELS[source.current_config.role],
    status,
    statusLabel: STATUS_LABELS[status],
    isActive: source.is_active,
    lastAttemptAt: source.last_attempt_at,
    lastAttemptStatusLabel: source.last_attempt_status
      ? INGESTION_STATUS_LABELS[source.last_attempt_status]
      : '—',
    lastSuccessAt: source.last_success_at,
    nextRefreshAt: source.next_refresh_at,
    lastError: source.last_error,
    format: source.current_config.format,
    crs: source.current_config.crs,
    refreshFrequency: source.current_config.refresh_frequency,
    refreshFrequencyLabel: source.current_config.refresh_frequency
      ? REFRESH_LABELS[source.current_config.refresh_frequency]
      : 'Non pianificato',
    redistributable: source.current_config.redistributable,
    mappingCount: source.current_config.mappings.length,
  };
};

/**
 * Internal references come from the versioned runtime catalog and, when available,
 * artifact provenance. These are NOT SecapDataSource connectors: they have no
 * ingestion status, refresh schedule or source snapshots of their own.
 */
const INTERNAL_STRATEGY_LABELS: Readonly<Record<SecapSourceStrategy, string>> = {
  public_reuse: 'Riuso di dati pubblici',
  hybrid: 'Elaborazione mista',
  internal_development: 'Elaborazione interna',
  composition: 'Composizione EDI',
  entity_data: 'Dato ente',
};

const PRODUCTION_ORIGIN_LABELS: Readonly<Record<string, string>> = {
  secap_execution: 'Esecuzione SECAP',
  urban_exposure_reuse: 'Riuso Urban Exposure',
};

const stringProperty = (
  object: Readonly<Record<string, unknown>> | null,
  property: string,
): string | null => {
  const value = object?.[property];
  return typeof value === 'string' && value.trim() ? value.trim() : null;
};

interface InternalSourceAccumulator {
  key: string;
  name: string;
  code: string;
  codeLabel: string;
  strategyLabel: string;
  indicators: Map<string, string>;
  modules: Set<string>;
  productionOrigins: Set<string>;
}

const internalReference = (indicator: ISecapRuntimeIndicator): {
  key: string;
  name: string;
  code: string;
  codeLabel: string;
} => {
  const publicCode = indicator.public_source_code?.trim();
  const moduleId = indicator.artifacts
    .map((artifact) => stringProperty(artifact.production_provenance, 'module_id'))
    .find((value): value is string => value !== null);

  if (publicCode) {
    return {
      key: `public:${publicCode}`,
      name: `Fonte integrata ${publicCode}`,
      code: publicCode,
      codeLabel: 'Codice fonte',
    };
  }
  if (moduleId) {
    return {
      key: `module:${moduleId}`,
      name: `Modulo EDI ${moduleId}`,
      code: moduleId,
      codeLabel: 'Modulo',
    };
  }
  return {
    key: `indicator:${indicator.code}`,
    name: `Elaborazione per ${indicator.name}`,
    code: indicator.code,
    codeLabel: 'Indicatore',
  };
};

export const buildSecapInternalSources = (
  catalog: ISecapRuntimeCatalog | null,
): readonly ISecapInternalSourceRowViewModel[] => {
  const grouped = new Map<string, InternalSourceAccumulator>();

  for (const indicator of catalog?.indicators ?? []) {
    // Entity-managed sources are already represented by the backoffice sources.
    if (
      indicator.row_type === 'entity_data' ||
      indicator.source_strategy === 'entity_data' ||
      indicator.origin === 'provided_by_entity'
    ) {
      continue;
    }

    const reference = internalReference(indicator);
    const key = `${indicator.source_strategy ?? 'unspecified'}:${reference.key}`;
    const source = grouped.get(key) ?? {
      ...reference,
      key,
      strategyLabel: indicator.source_strategy
        ? INTERNAL_STRATEGY_LABELS[indicator.source_strategy]
        : 'Strategia non specificata',
      indicators: new Map<string, string>(),
      modules: new Set<string>(),
      productionOrigins: new Set<string>(),
    };
    source.indicators.set(indicator.code, indicator.name);

    for (const artifact of indicator.artifacts) {
      const moduleId = stringProperty(artifact.production_provenance, 'module_id');
      const moduleVersion = stringProperty(artifact.production_provenance, 'module_version');
      if (moduleId) {
        source.modules.add(moduleVersion ? `${moduleId} · v${moduleVersion}` : moduleId);
      }
      const origin = stringProperty(artifact.production_provenance, 'production_origin');
      if (origin) {
        source.productionOrigins.add(PRODUCTION_ORIGIN_LABELS[origin] ?? origin);
      }
    }
    grouped.set(key, source);
  }

  return [...grouped.values()].map((source) => ({
    key: source.key,
    name: source.name,
    code: source.code,
    codeLabel: source.codeLabel,
    strategyLabel: source.strategyLabel,
    indicators: [...source.indicators].map(([code, name]) => ({ code, name })),
    modules: [...source.modules],
    productionOrigins: [...source.productionOrigins],
  }));
};

export const buildSecapSourcesViewModel = (
  dataSources: readonly ISecapProductDataSource[],
  runtimeCatalog: ISecapRuntimeCatalog | null,
): ISecapSourcesViewModel => ({
  summary: {
    planSources: dataSources.filter((source) => source.current_config.role === 'plan_source').length,
    comparisonSources: dataSources.filter(
      (source) => source.current_config.role === 'comparison',
    ).length,
    userUploads: dataSources.filter((source) => source.source_type === 'user_upload').length,
    activeSources: dataSources.filter((source) => source.is_active).length,
    errorSources: dataSources.filter(
      (source) => source.last_attempt_status === 'failed' || Boolean(source.last_error),
    ).length,
    total: dataSources.length,
  },
  sources: dataSources.map(toSourceRow),
  internalSources: buildSecapInternalSources(runtimeCatalog),
});
